import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./ui/button";
import { Mark } from "./Mark";
import { PageLink } from "./PageLink";
import { Pause, Play } from "lucide-react";
import { RunConsole, ChartAuditQueue, DataShieldCard, ConnectorWall, runs } from "./ProductVisuals";

/**
 * Home hero: four slides, each one capability or domain with a product-style visual.
 * Auto-advances every 7s; pauses on hover/focus; no autoplay under reduced motion.
 * The tab row doubles as the proof bar (each tab carries its published outcome).
 */
interface Slide {
  tab: string;
  proof: string;
  title: ReactNode;
  lede: string;
  link: { label: string; page: string };
  visual: ReactNode;
}

const slides: Slide[] = [
  {
    tab: "Agents with approvals",
    proof: "24× lending productivity",
    title: <>Agents do the work. People make <Mark>the call</Mark>.</>,
    lede: "Multi-step agents read, extract and draft. Anything with risk stops at a named person, and every step lands in the audit trail.",
    link: { label: "How agents run", page: "platform-agents" },
    visual: <RunConsole {...runs.loan} />,
  },
  {
    tab: "Healthcare chart audit",
    proof: "11× clinical throughput",
    title: <>Chart audit at 11× the pace. Clinicians <Mark>still decide</Mark>.</>,
    lede: "The Healthcare Domain Pack audits charts against chart-type rules with a confidence score and reasoning — at 99.4% accuracy in production.",
    link: { label: "See the Healthcare pack", page: "solutions-healthcare" },
    visual: <ChartAuditQueue />,
  },
  {
    tab: "Data Shield",
    proof: "All 10 OWASP GenAI risks addressed",
    title: <>Personal data never <Mark>reaches the model</Mark>.</>,
    lede: "Data Shield tokenises personal data before the prompt leaves your boundary. The model works on tokens; only your systems can reverse them.",
    link: { label: "How Data Shield works", page: "security" },
    visual: <DataShieldCard />,
  },
  {
    tab: "Integrations & MCP",
    proof: "MCP both ways · any REST API",
    title: <>Works with the systems you <Mark>already run</Mark>.</>,
    lede: "Native processors, OAuth2 apps and MCP in both directions — plus any authenticated REST API. No wholesale migration.",
    link: { label: "Browse connectors", page: "platform-integrations" },
    visual: <ConnectorWall />,
  },
];

const INTERVAL = 7000;

export function HeroCarousel({ onNavigate, onNavigateToDemo }: { onNavigate: (p: string) => void; onNavigateToDemo: () => void }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false); // hover or focus inside the carousel
  const [stopped, setStopped] = useState(false); // the visitor pressed Pause
  const [reduced, setReduced] = useState(false);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (paused || stopped || reduced) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % slides.length), INTERVAL);
    return () => window.clearTimeout(t);
  }, [i, paused, stopped, reduced]);

  const go = (n: number, focus = false) => {
    const next = (n + slides.length) % slides.length;
    setI(next);
    if (focus) tabsRef.current[next]?.focus();
  };

  const s = slides[i];

  return (
    <section
      className="bg-white border-b border-softblack-tint-20"
      aria-roledescription="carousel"
      aria-label="What TurfAI does"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="container mx-auto px-4 pt-10 lg:pt-12">
        <div
          key={i}
          id={`hero-slide-${i}`}
          role="tabpanel"
          aria-roledescription="slide"
          aria-label={`${i + 1} of ${slides.length}: ${s.tab}`}
          className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16 lg:min-h-[600px]"
        >
          <div className="tick-in">
            <div className="h-1 w-[72px] bg-vibrant-orange" aria-hidden="true" />
            <h1 className="mt-7 text-[40px] sm:text-[50px] lg:text-[60px] leading-[1.05] font-bold tracking-tight text-dark-indigo">
              {s.title}
            </h1>
            <p className="mt-6 max-w-[34em] text-[19px] lg:text-[20px] leading-relaxed text-soft-black">{s.lede}</p>
            <div className="mt-9 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-7">
              <Button variant="cta" size="cta" onClick={onNavigateToDemo}>
                Schedule a demo
              </Button>
              <PageLink
                page={s.link.page}
                onNavigate={onNavigate}
                className="inline-flex items-center gap-1.5 text-[16px] font-bold text-royal-indigo hover:text-dark-indigo"
              >
                {s.link.label}
              </PageLink>
            </div>
          </div>
          <div className="w-full max-w-[560px] lg:justify-self-end">{s.visual}</div>
        </div>

        {/* Tabs double as the proof bar */}
        <div className="relative">
        {!reduced && (
          <button
            type="button"
            onClick={() => setStopped((v) => !v)}
            aria-label={stopped ? "Play slides" : "Pause slides"}
            className="absolute -top-11 right-0 inline-flex h-9 items-center gap-1.5 rounded px-2 text-[13px] font-semibold text-royal-indigo hover:bg-indigo-tint-05 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo"
          >
            {stopped ? <Play className="h-4 w-4" aria-hidden="true" /> : <Pause className="h-4 w-4" aria-hidden="true" />}
            {stopped ? "Play" : "Pause"}
          </button>
        )}
        <div
          role="tablist"
          aria-label="Choose a slide"
          className="mt-8 grid grid-cols-2 lg:grid-cols-4 border-t border-softblack-tint-20"
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") go(i + 1, true);
            if (e.key === "ArrowLeft") go(i - 1, true);
          }}
        >
          {slides.map((sl, n) => {
            const on = n === i;
            return (
              <button
                key={sl.tab}
                ref={(el) => (tabsRef.current[n] = el)}
                role="tab"
                aria-selected={on}
                aria-controls={`hero-slide-${n}`}
                tabIndex={on ? 0 : -1}
                onClick={() => go(n)}
                className="group relative py-5 pr-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo"
              >
                <span className="absolute left-0 right-6 top-[-1px] h-[3px] bg-transparent" aria-hidden="true">
                  {on && (
                    <span
                      className={`block h-full bg-royal-indigo ${reduced || stopped ? "w-full" : "hero-progress"} ${paused ? "[animation-play-state:paused]" : ""}`}
                    />
                  )}
                </span>
                <span className={`block font-display text-[16px] font-semibold ${on ? "text-dark-indigo" : "text-soft-black group-hover:text-royal-indigo"}`}>
                  {sl.tab}
                </span>
                <span className="mt-1 block text-[13px] text-soft-black">{sl.proof}</span>
              </button>
            );
          })}
        </div>
        </div>
      </div>
    </section>
  );
}
